import { api } from '../utils/apiClient';

let settingsCache = null;
let cacheTimestamp = null;
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Get all settings from the backend
 */
export const getAllSettings = async () => {
  try {
    const response = await api.get('/api/settings');
    return response.data.settings || {};
  } catch (error) {
    console.error('Failed to fetch settings:', error);
    return {};
  }
};

/**
 * Get a specific setting value
 */
export const getSetting = async (key, defaultValue = null) => {
  try {
    // Check cache first
    if (settingsCache && cacheTimestamp && Date.now() - cacheTimestamp < CACHE_TTL_MS) {
      return settingsCache[key] !== undefined ? settingsCache[key] : defaultValue;
    }

    // Fetch from backend
    const settings = await getAllSettings();
    settingsCache = settings;
    cacheTimestamp = Date.now();
    return settings[key] !== undefined ? settings[key] : defaultValue;
  } catch (error) {
    console.error(`Failed to fetch setting ${key}:`, error);
    return defaultValue;
  }
};

/**
 * Get cached setting (synchronous, uses cache if available)
 */
export const getCachedSetting = (key, defaultValue = null) => {
  if (settingsCache && cacheTimestamp && Date.now() - cacheTimestamp < CACHE_TTL_MS) {
    return settingsCache[key] !== undefined ? settingsCache[key] : defaultValue;
  }
  return defaultValue;
};

/**
 * Update a setting
 */
export const updateSetting = async (key, value) => {
  try {
    await api.put(`/api/settings/${key}`, { value });
    // Invalidate cache
    settingsCache = null;
    cacheTimestamp = null;
    return true;
  } catch (error) {
    console.error(`Failed to update setting ${key}:`, error);
    throw error;
  }
};

/**
 * Update multiple settings at once
 */
export const updateSettings = async (settings) => {
  try {
    await api.put('/api/settings', { settings });
    // Invalidate cache
    settingsCache = null;
    cacheTimestamp = null;
    return true;
  } catch (error) {
    console.error('Failed to update settings:', error);
    throw error;
  }
};

/**
 * Reset all settings to defaults
 */
export const resetSettingsToDefaults = async () => {
  try {
    await api.post('/api/settings/reset');
    // Invalidate cache
    settingsCache = null;
    cacheTimestamp = null;
    return true;
  } catch (error) {
    console.error('Failed to reset settings:', error);
    throw error;
  }
};

/**
 * Preload settings into cache
 */
export const preloadSettings = async () => {
  try {
    const settings = await getAllSettings();
    settingsCache = settings;
    cacheTimestamp = Date.now();
    return settings;
  } catch (error) {
    console.error('Failed to preload settings:', error);
    return {};
  }
};

/**
 * Clear settings cache
 */
export const clearSettingsCache = () => {
  settingsCache = null;
  cacheTimestamp = null;
};

// Helper functions for common settings
export const getDashboardRefreshInterval = () => getCachedSetting('dashboardRefreshInterval', 30);
export const getUptimeCheckFrequency = () => getCachedSetting('uptimeCheckFrequency', 5);
export const getHealthScanInterval = () => getCachedSetting('healthScanInterval', 60);
export const getSeverityThresholds = () => ({
  healthy: getCachedSetting('severityThresholdHealthy', 95),
  warning: getCachedSetting('severityThresholdWarning', 80),
  critical: getCachedSetting('severityThresholdCritical', 60),
});
export const getTablePaginationSize = () => getCachedSetting('tablePaginationSize', 20);
export const getDefaultFilter = () => getCachedSetting('defaultFilter', 'all');
export const getUITheme = () => getCachedSetting('uiTheme', 'light');
export const getApiRateLimit = () => getCachedSetting('apiRateLimit', 100);

export const getLogsExportFormats = () => {
  const formats = getCachedSetting('exportFormats', ['csv', 'json', 'pdf']);
  if (!Array.isArray(formats)) {
    return ['csv', 'json', 'pdf'];
  }

  const normalized = formats
    .map((f) => String(f).toLowerCase())
    .filter((f) => f === 'csv' || f === 'json' || f === 'pdf');

  return normalized.length > 0 ? Array.from(new Set(normalized)) : ['csv', 'json', 'pdf'];
};

